import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Plus, Pencil, Trash2 } from "lucide-react";

import BannerForm from "./BannerForm";
import { listBanners, createBanner, updateBanner, deleteBanner } from "./bannerApi";

import ConfirmDialog from "../../../components/common/ConfirmDialog";
import FormButton from "../../../components/form/FormButton";
import useTableController from "../../../components/table/useTableController";
import TableToolbar from "../../../components/table/TableToolbar";
import Paginator from "../../../components/table/Paginator";

import "./banner.css";

const normType = (value) => {
  const key = String(value || "").toLowerCase();

  if (key === "external") return "external";
  if (key === "venue") return "venue";

  return "listing";
};

const prettyType = (type) => {
  const key = normType(type);

  if (key === "external") return "External";
  if (key === "venue") return "Venue";

  return "Listing";
};

const TypePill = ({ type }) => {
  const key = normType(type);

  return (
    <span
      className={
        key === "external"
          ? "pill pill-external"
          : key === "venue"
          ? "pill pill-venue"
          : "pill pill-listing"
      }
    >
      {prettyType(key)}
    </span>
  );
};

const BannersPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const tc = useTableController(items, {
    searchKeys: ["title", "redirectValue"],
    initialPageSize: 10,
    normalizers: { redirectType: normType },
  });

  const load = async () => {
    setLoading(true);

    try {
      const data = await listBanners();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (payload) => {
    try {
      await createBanner(payload);
      toast.success("Banner created");
      setCreating(false);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Create failed");
      throw error;
    }
  };

  const handleUpdate = async (payload) => {
    try {
      const id = editing?._id || editing?.id;

      await updateBanner(id, payload);
      toast.success("Banner updated");
      setEditing(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Update failed");
      throw error;
    }
  };

  const handleDelete = async () => {
    const id = deleting?._id || deleting?.id;

    if (!id) {
      toast.error("Missing banner id");
      return;
    }

    try {
      setBusyId(id);
      await deleteBanner(id);
      toast.success("Banner deleted");
      setDeleting(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="sa-head-row banner-head-row">
        <h1 className="sa-h1 sa-topbar-managed-page-title">Banners</h1>

        <FormButton
          variant="primary"
          className="banner-new-btn"
          onClick={() => setCreating(true)}
        >
          <Plus className="lucide" /> New Banner
        </FormButton>
      </div>

      <TableToolbar
        searchTerm={tc.searchTerm}
        onSearchChange={tc.setSearch}
        filterDefs={[
          {
            id: "redirectType",
            label: "Type",
            options: [
              { value: "all", label: "All types" },
              { value: "external", label: "External" },
              { value: "listing", label: "Listing" },
              { value: "venue", label: "Venue" },
            ],
          },
        ]}
        filters={tc.filters}
        onFilterChange={tc.setFilter}
        searchPlaceholder="Search banners..."
      />

      <div className="sa-card">
        {loading ? (
          <div className="sa-placeholder" style={{ height: 120 }}>
            Loading…
          </div>
        ) : tc.total === 0 ? (
          <div className="sa-empty">
            <div>No banners found.</div>

            <FormButton variant="primary" onClick={() => setCreating(true)}>
              Create your first banner
            </FormButton>
          </div>
        ) : (
          <div className="sa-table-wrap banner-table-wrap">
            <table className="sa-table banner-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>S.N.</th>
                  <th style={{ width: 120 }}>Preview</th>

                  <th onClick={() => tc.toggleSort("title")} style={{ cursor: "pointer" }}>
                    Title{" "}
                    {tc.sortKey === "title" ? (tc.sortDir === "asc" ? "▲" : "▼") : ""}
                  </th>

                  <th>Type</th>
                  <th>Scope</th>
                  <th>Value</th>

                  <th
                    onClick={() => tc.toggleSort("priority")}
                    style={{ cursor: "pointer", width: 110 }}
                  >
                    Priority{" "}
                    {tc.sortKey === "priority"
                      ? tc.sortDir === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>

                  <th
                    onClick={() => tc.toggleSort("createdAt")}
                    style={{ cursor: "pointer", width: 140 }}
                  >
                    Created{" "}
                    {tc.sortKey === "createdAt"
                      ? tc.sortDir === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>

                  <th style={{ width: 110 }}>Status</th>
                  <th style={{ textAlign: "right", width: 100 }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {tc.rows.map((banner, index) => {
                  const id = banner._id || banner.id;

                  return (
                    <tr key={id || `${banner.title}-${index}`}>
                      <td>{(tc.page - 1) * tc.pageSize + index + 1}</td>

                      <td>
                        <div className="banner-table-preview">
                          {banner.imageUrl ? (
                            <img src={banner.imageUrl} alt="" />
                          ) : (
                            <span>—</span>
                          )}
                        </div>
                      </td>

                      <td>{banner.title || "—"}</td>

                      <td>
                        <TypePill type={banner.redirectType} />
                      </td>

                      <td>
                        {banner.isGlobal ? (
                          <span className="pill pill-global">Global</span>
                        ) : (
                          <span className="pill pill-country">
                            {banner.country || "Country"}
                          </span>
                        )}
                      </td>

                      <td className="sa-ellipsis" title={banner.redirectValue || ""}>
                        {banner.redirectValue || "—"}
                      </td>

                      <td>{banner.priority ?? "—"}</td>

                      <td>
                        {banner.createdAt
                          ? new Date(banner.createdAt).toLocaleDateString()
                          : "—"}
                      </td>

                      <td>
                        {banner.active === false ? (
                          <span className="pill pill-inactive">Inactive</span>
                        ) : (
                          <span className="pill pill-active">Active</span>
                        )}
                      </td>

                      <td className="sa-actions-cell">
                        <div className="sa-actions">
                          <button
                            type="button"
                            className="sa-icon-link"
                            onClick={() => setEditing(banner)}
                            title="Edit banner"
                            aria-label="Edit banner"
                          >
                            <Pencil className="lucide" />
                          </button>

                          <button
                            type="button"
                            className="sa-icon-link danger"
                            onClick={() => setDeleting(banner)}
                            disabled={busyId === id}
                            title="Delete banner"
                            aria-label="Delete banner"
                          >
                            <Trash2 className="lucide" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <Paginator
              page={tc.page}
              totalPages={tc.totalPages}
              onPage={tc.setPage}
              pageSize={tc.pageSize}
              onPageSizeChange={tc.setPageSize}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          </div>
        )}
      </div>

      {creating && (
        <BannerForm
          open={creating}
          mode="create"
          onCancel={() => setCreating(false)}
          onSubmit={handleCreate}
        />
      )}

      {editing && (
        <BannerForm
          key={editing?._id || editing?.id}
          open={!!editing}
          mode="edit"
          initialData={{
            title: editing.title || "",
            imageUrl: editing.imageUrl || "",
            imageKey: editing.imageKey || "",
            redirectType: normType(editing.redirectType || "listing"),
            redirectValue: editing.redirectValue || "",
            priority: editing.priority ?? 1,
            isGlobal:
              typeof editing.isGlobal === "boolean" ? editing.isGlobal : true,
            country: editing.country || "",
            active: typeof editing.active === "boolean" ? editing.active : true,
          }}
          onCancel={() => setEditing(null)}
          onSubmit={handleUpdate}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title="Delete Banner"
        message={`Are you sure you want to delete "${
          deleting?.title || "this banner"
        }"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={busyId === (deleting?._id || deleting?.id)}
        onConfirm={handleDelete}
        onCancel={() => {
          if (busyId !== (deleting?._id || deleting?.id)) {
            setDeleting(null);
          }
        }}
      />
    </>
  );
};

export default BannersPage;